import React, { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchFilteredProducts, fetchTypeProducts } from '../../services';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import ProductCard from '../productCard';
import Spinner from '../spinner';
import './productType.scss';
import PageCollectionHeader from '../pageCollectionHeader';
import { InfiniteLoader } from '../infiniteLoader/infiniteLoader';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';
import { getProductTypeIcon } from '../../utils/productTypeIcons';
import CatalogEmptyState from '../catalogEmptyState/CatalogEmptyState';

const TAKE = 12;

const normalizeTypeName = (value: string) => value.trim().toLowerCase();

const getTypeAliases = (value: string) => {
  const normalized = normalizeTypeName(value);
  const aliases = [normalized];

  if (normalized.endsWith('s')) {
    aliases.push(normalized.slice(0, -1));
  } else {
    aliases.push(`${normalized}s`);
  }

  return aliases;
};

const ProductType = () => {
  const dispatch = useAppDispatch();
  const { type } = useParams();
  const { status, products, currentType, hasMore, skip, unqTypes } = useAppSelector(
    (state) => state.productsType
  );
  const loading = status === 'loading';
  const typeOptions = unqTypes ?? [];
  const routeType = type ?? '';
  const routeTypeAliases = getTypeAliases(routeType);
  const currentTypeMeta = typeOptions.find((item) =>
    routeTypeAliases.includes(normalizeTypeName(item.name))
  );
  const resolvedType = currentTypeMeta?.name ?? routeType;
  const typeIconUrl = getProductTypeIcon(resolvedType, currentTypeMeta?.image?.publicUrl);

  useScrollRestoration(!loading && products.length > 0, `type:${resolvedType || 'all'}`);

  const loadMore = useCallback(() => {
    if (!resolvedType || loading || !hasMore) return;

    const nextSkip = currentType === resolvedType ? skip : 0;
    dispatch(fetchFilteredProducts({ type: resolvedType, skip: nextSkip, take: TAKE }));
  }, [currentType, dispatch, hasMore, loading, resolvedType, skip]);

  useEffect(() => {
    if (resolvedType && (currentType !== resolvedType || products.length === 0)) {
      dispatch(fetchFilteredProducts({ type: resolvedType, skip: 0, take: TAKE }));
    }
  }, [currentType, dispatch, products.length, resolvedType]);

  useEffect(() => {
    if (typeOptions.length === 0) {
      dispatch(fetchTypeProducts());
    }
  }, [dispatch, typeOptions.length]);

  return (
    <div className="type-page-container">
      <PageCollectionHeader
        eyebrow="Collection"
        title={resolvedType}
        subtitle="Products filtered by type"
        imageUrl={typeIconUrl}
        imageAlt={resolvedType || 'Product type'}
      />
      {status === 'loading' && products.length === 0 ? (
        <Spinner />
      ) : status === 'failed' ? (
        <CatalogEmptyState
          role="alert"
          mark="!"
          eyebrow="Catalog unavailable"
          title={`We can’t load ${resolvedType || 'this collection'} right now.`}
          message="The product service is not responding. Start the backend and refresh this page to load this collection."
          primaryAction={{
            label: 'Refresh collection',
            to: `/products/types/${resolvedType}`,
            reloadDocument: true,
          }}
          secondaryAction={{ label: 'Go home', to: '/' }}
        />
      ) : products.length === 0 ? (
        <CatalogEmptyState
          eyebrow="Collection is empty"
          title={`No ${resolvedType || 'products'} available yet.`}
          message="This collection is not stocked right now. Keep browsing the full catalog or start with another jewelry type."
          primaryAction={{ label: 'View all jewelry', to: '/products' }}
          secondaryAction={{ label: 'Shop pendants', to: '/products/types/Pendants' }}
        />
      ) : (
        <InfiniteLoader loadMore={loadMore} hasMore={hasMore} loading={loading}>
          <div className="card-container">
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
              />
            ))}
          </div>

          {loading && <Spinner />}
        </InfiniteLoader>
      )}
    </div>
  );
};

export default ProductType;
